import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, 
  MapPin, 
  Leaf,
  Heart,
  Music,
  Flame,
  Info,
  X,
  Globe,
  TreeDeciduous,
  Sparkles
} from 'lucide-react';

// Données des familles shamaniques de Colombie
const shamanFamilies = [
  {
    id: 'siona-huitoto',
    name: 'Siona & Huitoto',
    region: 'Amazonie Sud',
    location: 'Putumayo, Leticia',
    coordinates: [-1.2, -75.5],
    type: 'Taita',
    practices: ['Yagé (Ayahuasca)', 'Mambé', 'Ambil'],
    symbol: 'Flamme Sacrée',
    description: 'Gardiens de la flamme sacrée dans les malocas de la forêt primaire. Leur mission inclut la défense écologique et culturelle.',
    color: '#8DC63F',
    roles: ['Guérison', 'Visions', 'Défense bioculturelle']
  },
  {
    id: 'kamentsa-inga',
    name: 'Kamentšá & Inga',
    region: 'Andes Sud',
    location: 'Nariño, Sibundoy',
    coordinates: [1.2, -77.0],
    type: 'Taita / Jaibana',
    practices: ['Yagé', 'Tabac sacré', 'Chants rituels'],
    symbol: 'Maloca & Musique',
    description: 'Tradition hybride entre pratiques amazoniennes et andines. Connus pour leurs chants et leur musique cérémonielle.',
    color: '#F4CC70',
    roles: ['Guérison hybride', 'Transmission musicale']
  },
  {
    id: 'embera-wounaan',
    name: 'Emberá & Wounaan',
    region: 'Côte Pacifique',
    location: 'Chocó, Nuquí',
    coordinates: [5.7, -77.3],
    type: 'Jaibana',
    practices: ['Plantes aquatiques', 'Rituels de l\'eau'],
    symbol: 'Eau & Forêt',
    description: 'Maîtres des rituels aquatiques, connectés aux rivières et à la mer. Soignent les maladies liées à l\'environnement.',
    color: '#61917E',
    roles: ['Lien environnement', 'Maladies de l\'eau']
  },
  {
    id: 'kogui-wiwa',
    name: 'Kogui, Arhuaco & Wiwa',
    region: 'Sierra Nevada',
    location: 'Sierra Nevada de Santa Marta',
    coordinates: [10.9, -73.9],
    type: 'Mamo',
    practices: ['Pagamento', 'Poporo (coca)', 'Divination'],
    symbol: 'Paiement à la Terre',
    description: 'Les "Grands Frères" de l\'humanité. Gardiens de l\'équilibre cosmique entre le monde visible et invisible.',
    color: '#36B1BF',
    roles: ['Équilibre cosmique', 'Gardiens de la Terre']
  },
  {
    id: 'muisca',
    name: 'Muisca (Historique)',
    region: 'Andes Centrales',
    location: 'Altiplano Cundiboyacense',
    coordinates: [5.0, -73.8],
    type: 'Chaman ancestral',
    practices: ['Rituels solaires', 'Or & Eau', 'Culte à Bachué'],
    symbol: 'Lagunes sacrées',
    description: 'Civilisation précolombienne légendaire. Leurs rituels dans les lagunes sacrées ont donné naissance à la légende de l\'Eldorado.',
    color: '#745EC5',
    roles: ['Rituels précolombiens', 'Héritage historique'],
    historical: true
  },
  {
    id: 'uitoto-tikuna',
    name: 'Uitoto & Tikuna',
    region: 'Amazonie Est',
    location: 'Leticia, Trapèze amazonien',
    coordinates: [-3.5, -70.0],
    type: 'Taita',
    practices: ['Mambé', 'Ambil', 'Cercle de parole'],
    symbol: 'Maloca ronde',
    description: 'Gardiens du cercle de parole (mambeadero). Transmission orale millénaire autour du mambé et de l\'ambil.',
    color: '#E07B53',
    roles: ['Cercle de parole', 'Transmission orale']
  }
];

// Données de l'arbre patrimonial
const treeData = {
  roots: ['Peuples premiers', 'Mythes fondateurs', 'Plantes sacrées (coca, yagé, tabac)', 'Pachamama'],
  trunk: 'Colombie - Berceau des traditions',
  branches: [
    { name: 'Amérique du Sud', items: ['Ayahuasca (Pérou)', 'Maté', 'Shipibo'] },
    { name: 'Afrique', items: ['Iboga (Gabon)', 'Sangomas', 'Féticheurs'] },
    { name: 'Asie', items: ['Ayurveda', 'MTC', 'Thés sacrés'] },
    { name: 'Europe', items: ['Druides', 'Hydromel', 'Herboristerie'] }
  ]
};

const ShamanMap = () => {
  const navigate = useNavigate();
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [showTree, setShowTree] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Positions sur la carte SVG (approximatives pour la Colombie)
  const getMapPosition = (coords) => {
    // Transformation simple des coordonnées en position SVG
    // Colombie: lat -4 à 12, lon -79 à -67
    const lat = coords[0];
    const lon = coords[1];
    const x = ((lon + 79) / 12) * 400 + 50; // Ajusté pour SVG 500px
    const y = ((12 - lat) / 16) * 500 + 30; // Ajusté pour SVG 560px
    return { x, y };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-950" data-testid="shaman-map">
      
      {/* Header */}
      <header className="border-b border-emerald-800/50 bg-emerald-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/tourism')}
                className="text-emerald-300 hover:text-emerald-100"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Retour
              </Button>
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Leaf className="w-5 h-5 text-emerald-400" />
                  Carte des Shamans
                </h1>
                <p className="text-sm text-emerald-400/70">
                  Colombie - Traditions vivantes
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowTree(!showTree)}
                className={`border-emerald-600 ${showTree ? 'bg-emerald-600 text-white' : 'text-emerald-300'}`}
                data-testid="toggle-tree"
              >
                <TreeDeciduous className="w-4 h-4 mr-2" />
                Arbre Patrimonial
              </Button>
              <Badge className="bg-emerald-600/20 text-emerald-300 border border-emerald-500/50">
                <Globe className="w-4 h-4 mr-2" />
                Prototype
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          
          {/* Carte SVG interactive */}
          <div className="lg:col-span-2">
            <Card className="bg-emerald-900/30 border-emerald-700/50 overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-400" />
                  Repérage des grandes familles shamaniques
                </CardTitle>
                <p className="text-sm text-emerald-400/70">
                  Chaque point représente un foyer de tradition - non nominatif par respect éthique
                </p>
              </CardHeader>
              <CardContent className="p-4">
                <div className="relative bg-emerald-950/50 rounded-xl p-4">
                  <svg 
                    viewBox="0 0 500 580" 
                    className="w-full h-auto"
                    style={{ maxHeight: '500px' }}
                  >
                    {/* Fond de carte stylisé Colombie */}
                    <defs>
                      <linearGradient id="colombiaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#134e4a" />
                        <stop offset="100%" stopColor="#064e3b" />
                      </linearGradient>
                      <filter id="glow">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                          <feMergeNode in="coloredBlur"/>
                          <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                      </filter>
                    </defs>
                    
                    {/* Contour simplifié de la Colombie */}
                    <path 
                      d="M200,520 Q180,420 150,380 Q110,330 130,270 Q150,220 120,160 Q110,100 180,50 Q245,5 290,55 Q370,110 420,160 Q480,235 430,330 Q400,370 380,460 Q365,520 260,580 Q210,550 200,520 Z" 
                      fill="url(#colombiaGrad)" 
                      stroke="#34d399" 
                      strokeWidth="2"
                      opacity="0.8"
                    />
                    
                    {/* Lignes de connexion (racines visuelles) */}
                    <path 
                      d="M250,550 Q230,580 180,590" 
                      stroke="#a78bfa" 
                      strokeWidth="3" 
                      fill="none" 
                      opacity="0.4"
                    />
                    <path 
                      d="M260,550 Q290,580 340,590" 
                      stroke="#a78bfa" 
                      strokeWidth="3" 
                      fill="none" 
                      opacity="0.4"
                    />
                    
                    {/* Points des familles shamaniques */}
                    {shamanFamilies.map((family, index) => {
                      const pos = getMapPosition(family.coordinates);
                      const isSelected = selectedFamily?.id === family.id;
                      
                      return (
                        <g key={family.id}>
                          {/* Cercle de glow si sélectionné */}
                          {isSelected && (
                            <circle
                              cx={pos.x}
                              cy={pos.y}
                              r="25"
                              fill={family.color}
                              opacity="0.3"
                              filter="url(#glow)"
                            />
                          )}
                          
                          {/* Point principal */}
                          <circle
                            cx={pos.x}
                            cy={pos.y}
                            r={isSelected ? "16" : "12"}
                            fill={family.color}
                            stroke={isSelected ? "#fff" : "#1e3a3a"}
                            strokeWidth={isSelected ? "3" : "2"}
                            className="cursor-pointer transition-all duration-300"
                            onClick={() => setSelectedFamily(family)}
                            style={{ 
                              filter: isSelected ? 'drop-shadow(0 0 8px ' + family.color + ')' : 'none'
                            }}
                          />
                          
                          {/* Indicateur historique */}
                          {family.historical && (
                            <text
                              x={pos.x}
                              y={pos.y + 4}
                              textAnchor="middle"
                              fontSize="10"
                              fill="#fff"
                              fontWeight="bold"
                            >
                              H
                            </text>
                          )}
                        </g>
                      );
                    })}
                    
                    {/* Légende */}
                    <g transform="translate(20, 480)">
                      <rect x="0" y="0" width="160" height="70" fill="#0d1f1c" rx="8" opacity="0.9"/>
                      <text x="10" y="20" fontSize="12" fill="#6ee7b7" fontWeight="bold">Légende</text>
                      <circle cx="20" cy="38" r="6" fill="#8DC63F"/>
                      <text x="32" y="42" fontSize="10" fill="#a7f3d0">Tradition vivante</text>
                      <circle cx="20" cy="56" r="6" fill="#745EC5"/>
                      <text x="32" y="60" fontSize="10" fill="#a7f3d0">Historique (H)</text>
                    </g>
                  </svg>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Panel d'information */}
          <div className="space-y-6">
            
            {/* Détail famille sélectionnée */}
            <AnimatePresence mode="wait">
              {selectedFamily ? (
                <motion.div
                  key={selectedFamily.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                >
                  <Card className="bg-emerald-900/40 border-emerald-700/50">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <Badge 
                          style={{ backgroundColor: selectedFamily.color + '30', borderColor: selectedFamily.color, color: selectedFamily.color }}
                          className="border"
                        >
                          {selectedFamily.type}
                        </Badge>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedFamily(null)}
                          className="text-emerald-400"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <CardTitle className="text-white text-xl mt-2">
                        {selectedFamily.name}
                      </CardTitle>
                      <p className="text-emerald-400/70 text-sm flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {selectedFamily.region} - {selectedFamily.location}
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-emerald-100/80 text-sm leading-relaxed">
                        {selectedFamily.description}
                      </p>
                      
                      <div>
                        <h4 className="text-emerald-300 text-sm font-medium mb-2 flex items-center gap-1">
                          <Leaf className="w-4 h-4" />
                          Pratiques
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedFamily.practices.map((practice, i) => (
                            <Badge key={i} variant="outline" className="border-emerald-600/50 text-emerald-300 text-xs">
                              {practice}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-emerald-300 text-sm font-medium mb-2 flex items-center gap-1">
                          <Flame className="w-4 h-4" />
                          Symbole
                        </h4>
                        <p className="text-emerald-100/70 text-sm">{selectedFamily.symbol}</p>
                      </div>
                      
                      <div>
                        <h4 className="text-emerald-300 text-sm font-medium mb-2 flex items-center gap-1">
                          <Heart className="w-4 h-4" />
                          Rôles
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {selectedFamily.roles.map((role, i) => (
                            <Badge key={i} className="bg-emerald-800/50 text-emerald-200 text-xs">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <Card className="bg-emerald-900/30 border-emerald-700/50">
                    <CardContent className="p-6 text-center">
                      <Sparkles className="w-12 h-12 text-emerald-500/50 mx-auto mb-4" />
                      <p className="text-emerald-300/70">
                        Cliquez sur un point de la carte pour découvrir une famille shamanique
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Liste des familles */}
            <Card className="bg-emerald-900/30 border-emerald-700/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg">Familles recensées</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {shamanFamilies.map((family) => (
                  <div
                    key={family.id}
                    onClick={() => setSelectedFamily(family)}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      selectedFamily?.id === family.id 
                        ? 'bg-emerald-800/50 border border-emerald-600' 
                        : 'bg-emerald-950/30 hover:bg-emerald-900/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-4 h-4 rounded-full" 
                        style={{ backgroundColor: family.color }}
                      />
                      <div>
                        <p className="text-white text-sm font-medium">{family.name}</p>
                        <p className="text-emerald-400/60 text-xs">{family.region}</p>
                      </div>
                      {family.historical && (
                        <Badge variant="outline" className="ml-auto border-purple-500/50 text-purple-300 text-xs">
                          Historique
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Arbre Patrimonial (modal/drawer) */}
        <AnimatePresence>
          {showTree && (
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              className="mt-8"
            >
              <Card className="bg-emerald-900/40 border-emerald-700/50">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <TreeDeciduous className="w-5 h-5 text-emerald-400" />
                    Arbre Patrimonial - Du Tronc aux Branches Mondiales
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-4 gap-6">
                    {/* Racines */}
                    <div className="bg-amber-900/20 rounded-xl p-4 border border-amber-700/30">
                      <h4 className="text-amber-400 font-medium mb-3 flex items-center gap-2">
                        <span className="text-2xl">🌱</span> Racines
                      </h4>
                      <ul className="space-y-2">
                        {treeData.roots.map((root, i) => (
                          <li key={i} className="text-amber-200/80 text-sm flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"/>
                            {root}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {/* Tronc */}
                    <div className="bg-emerald-900/30 rounded-xl p-4 border border-emerald-600/30">
                      <h4 className="text-emerald-400 font-medium mb-3 flex items-center gap-2">
                        <span className="text-2xl">🪵</span> Tronc
                      </h4>
                      <p className="text-emerald-200/80 text-sm">{treeData.trunk}</p>
                      <div className="mt-4 space-y-2">
                        <Badge className="bg-emerald-800/50 text-emerald-200">Shamans</Badge>
                        <Badge className="bg-emerald-800/50 text-emerald-200">Médecines</Badge>
                        <Badge className="bg-emerald-800/50 text-emerald-200">Rituels</Badge>
                      </div>
                    </div>
                    
                    {/* Branches */}
                    <div className="md:col-span-2 bg-green-900/20 rounded-xl p-4 border border-green-700/30">
                      <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                        <span className="text-2xl">🌿</span> Branches Mondiales
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        {treeData.branches.map((branch, i) => (
                          <div key={i}>
                            <p className="text-green-300 font-medium text-sm mb-1">{branch.name}</p>
                            <ul className="space-y-1">
                              {branch.items.map((item, j) => (
                                <li key={j} className="text-green-200/60 text-xs">• {item}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Note éthique */}
                  <div className="mt-6 p-4 bg-slate-900/50 rounded-lg border border-slate-700/50">
                    <p className="text-slate-400 text-sm flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>
                        Cette carte respecte les principes éthiques de non-appropriation culturelle. 
                        Aucun nom personnel n'est affiché sans consentement explicite. 
                        Les données proviennent de sources publiques et de collaborations avec les communautés.
                      </span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      
      {/* Citation en bas */}
      <footer className="border-t border-emerald-900/50 py-6 mt-8">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-emerald-500/60 italic text-sm">
            "Arroser la graine, c'est prêter attention à la source de chaque soin, sa symbolique, l'intention posée"
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ShamanMap;
